import React from "react";
import {
  useProjectResourcesRetrieval,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneResources,
} from "@planzone/view-models";
import { Identifiable, project, Resource, Tag } from "@planzone/dto";
import { useWorkspaceContext } from "@scenes/workspaceContext";

function sanitize(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function match(name: string, search: string) {
  return new RegExp(sanitize(search), "i").test(name);
}

function filterSearch<T extends { name: string } | { label: string }>(
  list: T[],
  search: string,
): T[] {
  return list.filter((item) =>
    match("label" in item ? item.label : item.name, search),
  );
}

// Inside a project, its activated members, whole. Elsewhere the planzone resources are listed by page.
function useProjectMembers() {
  const ctx = useWorkspaceContext();
  const isInProject = ctx.type === "project";
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    projectResources,
    retrieveProjectResources,
  } = useProjectResourcesRetrieval(
    isInProject ? ctx.project : { ...project, uuid: "" },
  );

  React.useEffect(() => {
    isInProject &&
      !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectResources(ctx.project);
  }, []);

  return {
    isInProject,
    isRequestPending,
    members: projectResources
      .filter((projectResource) => !projectResource.deactivated)
      .map((projectResource) => projectResource.resource),
  };
}

// Allocating everyone or a tag outside a project needs every planzone resource: the whole list is only
// loaded then, and the allocation waits for it.
function useAllPlanzoneResources() {
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    resources,
    isRequestPending,
    isRequestSuccess,
    retrievePlanzoneResources,
  } = useRetrievePlanzoneResources();
  const pendingAllocation = React.useRef<
    ((resources: Identifiable<Resource>[]) => void) | null
  >(null);

  React.useEffect(() => {
    if (isRequestSuccess && pendingAllocation.current) {
      const allocate = pendingAllocation.current;

      pendingAllocation.current = null;
      allocate(resources);
    }
  }, [isRequestSuccess, resources]);

  return {
    isLoaded: isRequestSuccess,
    isRequestPending,
    resources,
    withAllResources(allocate: (resources: Identifiable<Resource>[]) => void) {
      if (isRequestSuccess) return allocate(resources);

      pendingAllocation.current = allocate;
      !isRequestPending && retrievePlanzoneResources(planzone);
    },
  };
}

function extractTags(resources: Identifiable<Resource>[]): Tag[] {
  return Object.values(
    resources.reduce<{ [key: string]: string }>(
      (tags, { tags: t }) =>
        t.reduce((acc, tag) => {
          const tagName = typeof tag === "string" ? tag : tag.name;

          return { ...acc, [tagName]: tagName };
        }, tags),
      {},
    ),
  ).map((name) => ({ name }));
}

function extractResourcesFromTag(
  resources: Identifiable<Resource>[],
  { name }: Tag,
) {
  return resources.filter(
    ({ tags }) => tags && !!tags.find((t) => t.name === name),
  );
}

export {
  filterSearch,
  useProjectMembers,
  useAllPlanzoneResources,
  extractTags,
  extractResourcesFromTag,
};
